import { useCallback, useEffect, useRef, useState } from 'react';

export interface CircularGestureOptions {
  centerX: number;
  centerY: number;
  snapDegrees: number;
  disabled?: boolean;
  onSnapCrossed?: (snappedBearing: number) => void;
  onDragStart?: (startBearing: number) => void;
  onDragEnd?: (
    finalBearing: number,
    sweepArc: { startDeg: number; endDeg: number; clockwise: boolean },
  ) => void;
}

export interface CircularGestureResult {
  angle: number;      // -1 when not dragging
  isDragging: boolean;
  handlers: {
    onPointerDown: React.PointerEventHandler<SVGElement>;
    onPointerMove: React.PointerEventHandler<SVGElement>;
    onPointerUp: React.PointerEventHandler<SVGElement>;
    onPointerCancel: React.PointerEventHandler<SVGElement>;
  };
}

interface DragState {
  lastSnappedBearing: number;
  startBearing: number;
  clockwise: boolean | null;
  firstMoveBearing: number | null;
  rafId: number | null;
  pointerDownTime: number;
  pointerDownX: number;
  pointerDownY: number;
}

/**
 * Convert SVG pointer coords to a compass bearing (0=North, CW, 0–359.999°).
 *
 * SVG coordinate system: x increases right, y increases DOWNWARD.
 * Compass: 0=North (visual up), 90=East, 180=South, 270=West (CW).
 * Conversion: bearing = atan2(dx, -dy) — negating dy flips the y axis
 * so that visual "up" (negative SVG y delta) maps to North (0°).
 */
function svgToBearing(
  svgX: number,
  svgY: number,
  cx: number,
  cy: number,
): number {
  const dx = svgX - cx;
  const dy = svgY - cy;
  return (Math.atan2(dx, -dy) * 180 / Math.PI + 360) % 360;
}

/** Snap a bearing to the nearest multiple of snapDegrees. */
function snapBearing(bearing: number, snapDegrees: number): number {
  return (Math.round(bearing / snapDegrees) * snapDegrees) % 360;
}

/**
 * Compute the angular delta between two bearings, returned in [-180, 180].
 * Positive = clockwise.
 */
function angleDelta(from: number, to: number): number {
  let d = to - from;
  // Normalise into [-180, 180]
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

/**
 * `useCircularGesture` — tracks pointer dragging around an SVG circular target.
 *
 * Uses `setPointerCapture` so the pointer is tracked globally within the SVG
 * even when the cursor leaves the element.  All state updates are batched
 * through `requestAnimationFrame` to cap re-renders to one per frame.
 */
export function useCircularGesture(options: CircularGestureOptions): CircularGestureResult {
  const { disabled } = options;

  const [gestureState, setGestureState] = useState<{ angle: number; isDragging: boolean }>({
    angle: -1,
    isDragging: false,
  });

  // Mutable drag bookkeeping — never triggers re-renders directly.
  const drag = useRef<DragState>({
    lastSnappedBearing: -1,
    startBearing: 0,
    clockwise: null,
    firstMoveBearing: null,
    rafId: null,
    pointerDownTime: 0,
    pointerDownX: 0,
    pointerDownY: 0,
  });

  // Keep option callbacks in a ref so handlers stay stable across renders.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  // If disabled flips true while dragging, reset drag state (RULE-089).
  useEffect(() => {
    if (disabled && gestureState.isDragging) {
      if (drag.current.rafId !== null) {
        cancelAnimationFrame(drag.current.rafId);
        drag.current.rafId = null;
      }
      setGestureState({ angle: -1, isDragging: false }); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleStateUpdate = useCallback(
    (newAngle: number, newIsDragging: boolean) => {
      if (drag.current.rafId !== null) {
        cancelAnimationFrame(drag.current.rafId);
      }
      drag.current.rafId = requestAnimationFrame(() => {
        drag.current.rafId = null;
        setGestureState({ angle: newAngle, isDragging: newIsDragging });
      });
    },
    [],
  );

  const onPointerDown: React.PointerEventHandler<SVGElement> = useCallback(
    (e) => {
      if (optionsRef.current.disabled) return;

      const cx = optionsRef.current.centerX;
      const cy = optionsRef.current.centerY;

      // Get the SVG element's bounding rect to convert page coords to SVG coords.
      const svgEl = e.currentTarget.closest('svg');
      if (!svgEl) return;
      const rect = svgEl.getBoundingClientRect();
      const viewBox = svgEl.viewBox.baseVal;
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;
      const svgX = (e.clientX - rect.left) * scaleX;
      const svgY = (e.clientY - rect.top) * scaleY;

      const bearing = svgToBearing(svgX, svgY, cx, cy);
      const snapped = snapBearing(bearing, optionsRef.current.snapDegrees);

      drag.current.lastSnappedBearing = snapped;
      drag.current.startBearing = snapped;
      drag.current.clockwise = null;
      drag.current.firstMoveBearing = null;
      drag.current.pointerDownTime = e.timeStamp;
      drag.current.pointerDownX = svgX;
      drag.current.pointerDownY = svgY;

      // Capture pointer so moves continue even outside the element.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* no-op if not supported */ }

      optionsRef.current.onDragStart?.(snapped);
      scheduleStateUpdate(bearing, true);
    },
    [scheduleStateUpdate],
  );

  const onPointerMove: React.PointerEventHandler<SVGElement> = useCallback(
    (e) => {
      if (optionsRef.current.disabled) return;
      if (!gestureState.isDragging && drag.current.rafId === null) return;

      const cx = optionsRef.current.centerX;
      const cy = optionsRef.current.centerY;

      const svgEl = e.currentTarget.closest('svg');
      if (!svgEl) return;
      const rect = svgEl.getBoundingClientRect();
      const viewBox = svgEl.viewBox.baseVal;
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;
      const svgX = (e.clientX - rect.left) * scaleX;
      const svgY = (e.clientY - rect.top) * scaleY;

      const bearing = svgToBearing(svgX, svgY, cx, cy);
      const snapped = snapBearing(bearing, optionsRef.current.snapDegrees);

      // Determine clockwise direction from first meaningful movement.
      if (drag.current.clockwise === null) {
        const delta = angleDelta(drag.current.startBearing, bearing);
        if (Math.abs(delta) > 2) {
          drag.current.clockwise = delta > 0;
          drag.current.firstMoveBearing = snapped;
        }
      }

      // Detect snap crossing.
      if (snapped !== drag.current.lastSnappedBearing) {
        drag.current.lastSnappedBearing = snapped;
        optionsRef.current.onSnapCrossed?.(snapped);
      }

      scheduleStateUpdate(bearing, true);
    },
    [gestureState.isDragging, scheduleStateUpdate],
  );

  const onPointerUp: React.PointerEventHandler<SVGElement> = useCallback(
    (e) => {
      if (optionsRef.current.disabled) return;

      const cx = optionsRef.current.centerX;
      const cy = optionsRef.current.centerY;

      const svgEl = e.currentTarget.closest('svg');
      if (!svgEl) return;
      const rect = svgEl.getBoundingClientRect();
      const viewBox = svgEl.viewBox.baseVal;
      const scaleX = viewBox.width / rect.width;
      const scaleY = viewBox.height / rect.height;
      const svgX = (e.clientX - rect.left) * scaleX;
      const svgY = (e.clientY - rect.top) * scaleY;

      const bearing = svgToBearing(svgX, svgY, cx, cy);
      const snapped = snapBearing(bearing, optionsRef.current.snapDegrees);

      const sweepArc = {
        startDeg: drag.current.startBearing,
        endDeg: snapped,
        clockwise: drag.current.clockwise ?? true,
      };

      // Tap threshold: if the pointer barely moved and was released quickly,
      // treat it as a tap and let the click handler on the direction node handle it.
      const tapDx = svgX - drag.current.pointerDownX;
      const tapDy = svgY - drag.current.pointerDownY;
      const tapDist = Math.sqrt(tapDx * tapDx + tapDy * tapDy);
      const tapDuration = e.timeStamp - drag.current.pointerDownTime;

      if (tapDist < 5 && tapDuration < 120) {
        // Tap — let the click handler on the direction node handle it.
        drag.current.lastSnappedBearing = -1;
        if (drag.current.rafId !== null) {
          cancelAnimationFrame(drag.current.rafId);
          drag.current.rafId = null;
        }
        setGestureState({ angle: -1, isDragging: false });
        return; // skip onDragEnd
      }

      if (drag.current.rafId !== null) {
        cancelAnimationFrame(drag.current.rafId);
        drag.current.rafId = null;
      }

      optionsRef.current.onDragEnd?.(snapped, sweepArc);
      setGestureState({ angle: -1, isDragging: false });
    },
    [],
  );

  const onPointerCancel: React.PointerEventHandler<SVGElement> = useCallback(
    () => {
      if (drag.current.rafId !== null) {
        cancelAnimationFrame(drag.current.rafId);
        drag.current.rafId = null;
      }
      // Cancel does NOT fire onDragEnd — gesture was interrupted.
      setGestureState({ angle: -1, isDragging: false });
    },
    [],
  );

  return {
    angle: gestureState.angle,
    isDragging: gestureState.isDragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
}
